import { Component, Inject, OnInit } from '@angular/core';
import { BzgModalService } from 'bz-genome-angular';
import { ViewDifferencesModalComponent } from './view-differences-modal/view-differences-modal.component';
import { OverlayService } from 'app/shared/services/overlay.service';
import { OVERLAY_PACKAGE_DATA, OverlayPackageData } from '../core/contracts/overlay-package.data';
import { DeploymentService } from '../core/services/deployment.service';
import { ShowToastService } from 'app/shared/services/showToast.service';
import { I18nService } from 'i18n/i18n.service';
import { DeltaItem } from './models/delta-item.model';

@Component({
  selector: 'biz-view-delta',
  templateUrl: './view-delta.component.html',
  styleUrls: ['./view-delta.component.scss']
})
export class ViewDeltaComponent implements OnInit {

  public _viewDeltaData: OverlayPackageData;
  public selectedCount: number = 0;
  public showDetail = false;
  public selectedItem: any = null;
  public selectedItemHasDifferences: boolean = false;
  public isDownloading: boolean = false;
  private selectedIds: string[] = [];
  // Data list state
  public isLoading: boolean = false;
  public deltaItems: DeltaItem[] = [];
  private allDeltaItems: DeltaItem[] = [];
  // Filters state (moved to parent)
  public filterName: string = '';
  public filterDeltaType: string[] | null = null;
  public filterObjType: string | null = null;
  public availableDeltaTypes: string[] = [];
  public availableObjTypes: string[] = [];
  public viewMode: 'basic' | 'expert' = 'basic';
  private basicTypes: string[] = [];

  constructor(
    @Inject(OVERLAY_PACKAGE_DATA) viewDeltaData: OverlayPackageData,
    private _overlayService: OverlayService,
    private _deploymentService: DeploymentService,
    private _showToastService: ShowToastService,
    private _i18nService: I18nService,
    private _modalService: BzgModalService,
  ) {
    this._viewDeltaData = viewDeltaData;
  }

  public ngOnInit() {
    const packageId = (this._viewDeltaData as any)?.packageId;
    if (!packageId) {
      this.deltaItems = [];
      return;
    }
    this.isLoading = true;
    // First, load basic types, then load delta data
    this._deploymentService.getDeltaBasicTypes().subscribe({
      next: (types) => {
        this.basicTypes = Array.isArray(types) ? (types as string[]) : [];
      },
      error: () => {
        this.basicTypes = [];
      },
      complete: () => {
        this._deploymentService.getDelta(packageId).subscribe({
          next: (delta: any) => {
            if (Array.isArray(delta)) {
              this.allDeltaItems = delta as DeltaItem[];
            } else if (delta) {
              this.allDeltaItems = [delta as DeltaItem];
            } else {
              this.allDeltaItems = [];
            }
            this.applyViewModeAndRefresh();
            this.isLoading = false;
          },
          error: () => {
            this.allDeltaItems = [];
            this.deltaItems = [];
            this.isLoading = false;
          }
        });
      }
    });
  }

  private updateAvailableOptions() {
    const unique = <T extends string>(arr: T[]) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b));
    this.availableDeltaTypes = unique(this.deltaItems.map(i => (i.deltaType || '') as string).filter(v => !!v));
    this.availableObjTypes = unique(this.deltaItems.map(i => (i.objType || '') as string).filter(v => !!v));
  }

  public onFiltersChanged(evt: { nameFilter: string; deltaType: string[] | null; type: string | null; viewMode?: 'basic' | 'expert' }) {
    this.filterName = evt?.nameFilter || '';
    this.filterDeltaType = evt?.deltaType || null;
    this.filterObjType = evt?.type || null;
    if (evt && evt.viewMode && this.viewMode !== evt.viewMode) {
      this.viewMode = evt.viewMode;
      this.applyViewModeAndRefresh();
    }
  }

  public closeViewDelta() {
    this._overlayService.hide();
  }

  public downloadSelected() {
    if (this.isDownloading) {
      return;
    }
    const packageId = (this._viewDeltaData as any)?.packageId;
    const objectIds: string[] = this.selectedIds || [];

    if (!packageId) {
      return; // No package id available
    }

    this.isDownloading = true;
    this._deploymentService
      .downloadDeltaReport(packageId, objectIds)
      .subscribe({
        next: (response: any) => {
            const blob = response.body instanceof Blob ? response.body : new Blob([response.body], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = 'DeltaReport.csv';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
        },
        error: (e) => {
          this._showToastService.showToast(this._i18nService.instant('VIEW_DELTA.DOWNLOAD_ERROR'), 'error');
          console.error(e);
          this.isDownloading = false;
        },
        complete: () => {
          this.isDownloading = false;
        }
      });
  }

  public onSelectedIdsChange(ids: string[]) {
    this.selectedIds = ids || [];
  }

  public openDetail(item: any) {
    this.selectedItem = item;
    this.selectedItemHasDifferences = this.itemHasDifferences(item);
    this.showDetail = true;
  }

  public backToList() {
    this.showDetail = false;
    this.selectedItem = null;
    this.selectedItemHasDifferences = false;
  }

  public openDifferencesModal() {
    if (!this.selectedItem) {
      return;
    }
    this._modalService.open(ViewDifferencesModalComponent, {
      closeOnBackdropClick: true,
      closeOnRouterNavigation: true,
      width: '70%',
      maxWidth: 'none',
      data: { item: this.selectedItem }
    });
  }

  private itemHasDifferences(item: any): boolean {
    if (!item) {
      return false;
    }
    const originalRaw = (item?.oldContent ?? '').toString();
    const modifiedRaw = (item?.newContent ?? '').toString();
    const hasOriginal = originalRaw.trim().length > 0;
    const hasModified = modifiedRaw.trim().length > 0;
    // Si no hay contenido en ambos, no hay nada que comparar
    if (!hasOriginal && !hasModified) {
      return false;
    }
    // Mostrar botón solo si hay diferencia entre los contenidos
    return originalRaw !== modifiedRaw;
  }

  private applyViewModeAndRefresh() {
    const isBasicType = (t: string | null | undefined) => !!t && this.basicTypes.includes(t);
    const source = this.allDeltaItems || [];

    let working = source;
    if (this.viewMode === 'basic' && this.basicTypes.length > 0) {
      working = source.filter(i => isBasicType(i?.objType as any));
    }

    // Always show basics first
    const withIndex = working.map((item, idx) => ({ item, idx }));
    withIndex.sort((a, b) => {
      const aBasic = isBasicType(a.item?.objType as any) ? 0 : 1;
      const bBasic = isBasicType(b.item?.objType as any) ? 0 : 1;
      if (aBasic !== bBasic) return aBasic - bBasic;
      return a.idx - b.idx;
    });
    this.deltaItems = withIndex.map(x => x.item);

    this.updateAvailableOptions();
  }
}
